import {
  Alert as MuiAlert,
  Button,
  CardContent,
} from "@mui/material";
import React from "react";
import styled from "@emotion/styled";
import { spacing } from "@mui/system";
import { useLocation, useNavigate } from "react-router-dom";
import OrderConfirmationMessage from "./OrderConfirmationMessage";
import { Payment } from "./payment-gateways/types";
import { formatToLocalDateTime } from "../../utils/dateUtils";
import { useCustomerPaymentInfo } from "./index";
import {
  OneSolomonAlert,
  OneSolomonContentPage,
  OneSolomonPageBackground,
  resolveIsCompanyOneSolomons,
} from "./oneSolomonUi";

const Alert = styled(MuiAlert)(spacing);

interface PaymentResultState extends Partial<Payment> {
  transaction_for?: string;
  company_image?: string;
  isCompanyOneSolomons?: boolean;
}

const SawfishPaymentResult: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { values: customerInfo } = useCustomerPaymentInfo();
  const paymentData = location.state as PaymentResultState;

  const isCompanyOneSolomons = resolveIsCompanyOneSolomons(
    customerInfo?.company_uuid,
    paymentData?.isCompanyOneSolomons,
    customerInfo?.isCompanyOneSolomons
  );

  const isPaymentSuccess = paymentData?.status === "completed";
  const isPaymentPending =
    paymentData?.status === "pending" ||
    paymentData?.status === "payment_initiated";

  if (!paymentData) {
    navigate("/", { replace: true });
    return null;
  }

  const content = (
    <>
      {isPaymentSuccess ? (
        <>
          {isCompanyOneSolomons ? (
            <OneSolomonAlert variant="success">
              {`Payment Successful! UUID: ${paymentData.uuid ?? "N/A"}${
                paymentData.paid_at || paymentData.created_at
                  ? `, DateTime: ${formatToLocalDateTime(
                      paymentData.paid_at ?? paymentData.created_at ?? null
                    )}`
                  : ""
              }`}
            </OneSolomonAlert>
          ) : (
            <Alert severity="success" my={3}>
              {`Payment Successful! UUID: ${paymentData.uuid ?? "N/A"}${
                paymentData.paid_at || paymentData.created_at
                  ? `, DateTime: ${formatToLocalDateTime(
                      paymentData.paid_at ?? paymentData.created_at ?? null
                    )}`
                  : ""
              }`}
            </Alert>
          )}

          <OrderConfirmationMessage />
        </>
      ) : isPaymentPending ? (
        <>
          {isCompanyOneSolomons ? (
            <OneSolomonAlert variant="info">
              Your payment request has been processed.
              <br />
              UUID: {paymentData.uuid ?? "N/A"}
              <br />
              DateTime:{" "}
              {formatToLocalDateTime(paymentData.created_at ?? null)}
            </OneSolomonAlert>
          ) : (
            <Alert severity="info" my={3}>
              Your payment request has been processed.
              <br />
              UUID: {paymentData.uuid ?? "N/A"}
              <br />
              DateTime:{" "}
              {formatToLocalDateTime(paymentData.created_at ?? null)}
            </Alert>
          )}

          <OrderConfirmationMessage />
        </>
      ) : (
        <>
          {isCompanyOneSolomons ? (
            <OneSolomonAlert variant="error">
              Uh oh! Your transaction could not be processed.
              <br />
              UUID: {paymentData.uuid ?? "N/A"}
              <br />
              Status: {paymentData.status ?? "Unknown"}
              {paymentData.status === "payment_initiation_rejected" &&
                paymentData.failed_reason && (
                  <>
                    <br />
                    Reason: {paymentData.failed_reason}
                  </>
                )}
            </OneSolomonAlert>
          ) : (
            <Alert severity="error" my={3}>
              Uh oh! Your transaction could not be processed.
              <br />
              UUID: {paymentData.uuid ?? "N/A"}
              <br />
              Status: {paymentData.status ?? "Unknown"}
              {paymentData.status === "payment_initiation_rejected" &&
                paymentData.failed_reason && (
                  <>
                    <br />
                    Reason: {paymentData.failed_reason}
                  </>
                )}
            </Alert>
          )}

          <Button
            onClick={() => navigate("/", { replace: true })}
            color="primary"
            variant="contained"
            fullWidth
            sx={{ mt: 3 }}
          >
            Please try again.
          </Button>
        </>
      )}
    </>
  );

  if (isCompanyOneSolomons) {
    return (
      <>
        <OneSolomonPageBackground />
        <OneSolomonContentPage>{content}</OneSolomonContentPage>
      </>
    );
  }

  return <CardContent>{content}</CardContent>;
};

export default SawfishPaymentResult;
